import { useEffect, useState } from "react";
import { Button, Input, Label, toast } from "@bloom/ui";
import { api } from "../api/client.ts";

interface Pricing {
  defaultEpisodePriceThb: number;
  defaultBundlePriceThb: number;
  launchPromoPriceThb: number;
  launchPromoEndAt: string | null;
  freeEpisodeCount: number;
}

interface Settings {
  pricing: Pricing;
}

export function PricingPage() {
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api
      .get<{ settings: Settings }>("/api/admin/settings")
      .then((d) => setPricing(d.settings.pricing));
  }, []);

  const save = async () => {
    if (!pricing) return;
    setSaving(true);
    try {
      await api.patch("/api/admin/settings", {
        pricing: {
          defaultEpisodePriceThb: pricing.defaultEpisodePriceThb,
          defaultBundlePriceThb: pricing.defaultBundlePriceThb,
          launchPromoPriceThb: pricing.launchPromoPriceThb,
          launchPromoEndAt: pricing.launchPromoEndAt,
          freeEpisodeCount: pricing.freeEpisodeCount,
        },
      });
      toast.success("Pricing saved");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!pricing) return <div>…</div>;

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="font-heading text-3xl font-bold">Pricing</h1>
      <p className="text-sm text-[var(--brand-muted)]">
        Stripe minimum is ฿10 per transaction. Episode unit is sold in packs of ≥ 2.
      </p>
      <Field label="Episode unit price (THB)">
        <Input
          type="number"
          value={pricing.defaultEpisodePriceThb}
          onChange={(e) =>
            setPricing({ ...pricing, defaultEpisodePriceThb: Number(e.target.value) })
          }
        />
      </Field>
      <Field label="Bundle price (THB)">
        <Input
          type="number"
          value={pricing.defaultBundlePriceThb}
          onChange={(e) =>
            setPricing({ ...pricing, defaultBundlePriceThb: Number(e.target.value) })
          }
        />
      </Field>
      <Field label="Launch promo price (THB)">
        <Input
          type="number"
          value={pricing.launchPromoPriceThb}
          onChange={(e) => setPricing({ ...pricing, launchPromoPriceThb: Number(e.target.value) })}
        />
      </Field>
      <Field label="Promo ends at (ISO datetime, empty = inactive)">
        <Input
          type="datetime-local"
          value={pricing.launchPromoEndAt ? pricing.launchPromoEndAt.slice(0, 16) : ""}
          onChange={(e) =>
            setPricing({
              ...pricing,
              launchPromoEndAt: e.target.value ? new Date(e.target.value).toISOString() : null,
            })
          }
        />
      </Field>
      <Field label="Free episode count">
        <Input
          type="number"
          value={pricing.freeEpisodeCount}
          onChange={(e) => setPricing({ ...pricing, freeEpisodeCount: Number(e.target.value) })}
        />
      </Field>
      <Button onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
